import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useEmployeesMembershipDataTable,
} from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { InlineEmployee } from 'modules/employees/components/InlineEmployee'
import { useApplicationDetailsRouterParams } from 'modules/software_management/hooks/useApplicationDetailsRouterParams'

export const ApplicationEmployees = () => {
  const { debouncedSearch, search, onSearch } = useDebouncedSearch()
  const { navigation } = useNavigation()
  const { softwareSetup } = useApplicationDetailsRouterParams()
  const source = useEmployeesMembershipDataTable(
    [
      {
        identifier: 'employee_fullName',
        title: i18n.t('software_management.software_setups.tabs.employees.table.name'),
        kind: ProjectionKind.Custom,
        render: ({ employee }) => {
          return <InlineEmployee employee={employee} variant="medium" />
        },
        template: '{{ employee_fullName }}',
      },
      {
        identifier: 'employee_email',
        title: i18n.t('software_management.software_setups.tabs.employees.table.email'),
        kind: ProjectionKind.Custom,
        render: ({ employee }) => {
          return <Text.Normal size="200">{employee?.email}</Text.Normal>
        },
        template: '{{ employee_email }}',
      },
      {
        identifier: 'employee_team',
        title: i18n.t('software_management.software_setups.tabs.employees.table.team'),
        kind: ProjectionKind.Custom,
        render: ({ employee }) => {
          return <Tag inline>{employee?.memberships[0]?.team?.name}</Tag>
        },
        template: '{{ employee.memberships[0].team.name }}',
      },
      {
        identifier: 'employee_job',
        title: i18n.t('software_management.software_setups.tabs.employees.table.job'),
        kind: ProjectionKind.Custom,
        render: ({ employee }) => {
          return <Tag inline>{employee?.referenceContract?.jobTitle}</Tag>
        },
        template: '{{ employee_referenceContract_jobTitle }}',
      },
      {
        identifier: 'startsOn',
        title: i18n.t('software_management.software_setups.tabs.employees.table.member_since'),
        kind: ProjectionKind.Custom,
        render: ({ employee }) => {
          return (
            <Text.Normal size="200">
              {employee?.referenceContract?.startsOn
                ? dateBackendDateTimeToUI(employee?.referenceContract?.startsOn)
                : ''}
            </Text.Normal>
          )
        },
        template: '{{ employee_referenceContract_startsOn }}',
      },
      {
        identifier: 'location',
        title: i18n.t('software_management.software_setups.tabs.employees.table.location'),
        kind: ProjectionKind.Custom,
        render: ({ employee }) => {
          return <Tag inline>{employee.locations[0]?.location?.name}</Tag>
        },
        template: '{{ employee.locations[0].name }}',
      },
    ],
    {
      variables: {
        softwareSetupIds: [softwareSetup.id],
        search: debouncedSearch,
      },
    }
  )

  return (
    <Box paddingTop="s32" gap="s32">
      <DataTable
        source={source}
        exportFileName={i18n.t(
          'software_management.software_setups.tabs.employees.table.export_file_name'
        )}
        sort={{ path: 'id', direction: 'desc' }}
        search={{
          placeholder: i18n.t('common.search'),
          value: search,
          onChange: onSearch,
        }}
        rowTo={({ employee }) =>
          employee.isTerminating
            ? navigation.software.applications.terminatedEmployees.employeeId.fullPath({
                employeeId: employee.id,
              })
            : navigation.software.employees.employeeId.fullPath({ employeeId: employee.id })
        }
      />
    </Box>
  )
}
